import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Toast, ToastContextType } from '../types/toast';

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((toastData: Omit<Toast, 'id' | 'timestamp'>): string => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const timestamp = Date.now();
    
    const newToast: Toast = {
      ...toastData,
      id,
      timestamp,
      // Default duration based on type if not specified
      duration: toastData.duration !== undefined ? toastData.duration : 
        toastData.type === 'success' ? 4000 :
        toastData.type === 'info' ? 5000 :
        toastData.type === 'warning' ? 6000 :
        toastData.type === 'error' ? 7000 : 5000
    };

    setToasts(prev => {
      // Limit to maximum 3 toasts - remove oldest if needed
      const updatedToasts = prev.length >= 3 ? prev.slice(1) : prev;
      return [...updatedToasts, newToast];
    });

    // Auto-dismiss if duration is set
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        dismissToast(id);
      }, newToast.duration);
    }

    // Handle action timeouts
    if (newToast.actions) {
      newToast.actions.forEach(action => {
        if (action.timeout) {
          setTimeout(() => {
            setToasts(prev => prev.map(toast => 
              toast.id === id 
                ? { ...toast, actions: toast.actions?.filter(a => a !== action) }
                : toast
            ));
          }, action.timeout);
        }
      });
    }

    return id;
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const value: ToastContextType = {
    toasts,
    showToast,
    dismissToast,
    clearAllToasts
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

